// src/components/UserInfo.jsx - Versione completa corretta con ferie
import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import UserQRCode from './UserQRCode';
import './userQRCode.css';
import './dashboard.css';
import './userInfo.css';

const UserInfo = ({ selectedMonth, setSelectedMonth, selectedYear, setSelectedYear }) => {
  const [availableMonths, setAvailableMonths] = useState([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Ottieni la data corrente
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() restituisce 0-11
    const currentYear = currentDate.getFullYear();

    // Calcola il mese precedente
    let previousMonth = currentMonth - 1;
    let previousYear = currentYear;

    // Gestisci il caso di gennaio (in cui il mese precedente è dicembre dell'anno precedente)
    if (previousMonth === 0) {
      previousMonth = 12;
      previousYear = currentYear - 1;
    }

    // LOGICA CORRETTA:
    // - Se siamo nei primi 4 giorni del mese (1-4), mostra il mese precedente come default
    // - Dal giorno 5 in poi, mostra sempre il mese corrente come default
    const defaultMonth = currentDay <= 4 ? previousMonth.toString() : currentMonth.toString();
    const defaultYear = currentDay <= 4 && currentMonth === 1 ? previousYear.toString() : currentYear.toString();

    // Costruisci l'array dei mesi disponibili
    const months = [
      { value: currentMonth.toString(), label: `${getMonthName(currentMonth)} ${currentYear}`, year: currentYear.toString() },
      { value: previousMonth.toString(), label: `${getMonthName(previousMonth)} ${previousYear}`, year: previousYear.toString() }
    ];

    setAvailableMonths(months);

    // Se il mese selezionato non è tra quelli disponibili, imposta il mese di default
    if (!selectedMonth || !selectedYear) {
      setSelectedMonth(defaultMonth);
      setSelectedYear(defaultYear);
    }
  }, [setSelectedMonth, setSelectedYear, selectedMonth, selectedYear]);

  // Carica i dati utente
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const userDocRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setUserData({
            id: currentUser.uid,
            email: currentUser.email,
            ...userDoc.data()
          });
        } else {
          setUserData({
            id: currentUser.uid,
            email: currentUser.email
          });
        }
      } catch (err) {
        console.error("Errore nel recupero dei dati utente:", err);
      }
    };

    fetchUserData();
  }, []);

  // Carica le statistiche mensili basate sugli stessi dati della tabella
  useEffect(() => {
    const fetchMonthlyStats = async () => {
      if (!selectedMonth || !selectedYear || !userData?.id) return;
      
      setIsLoadingStats(true);
      try {
        // Normalizza il mese (rimuovi eventuali prefissi e zeri iniziali)
        let normalizedMonth = selectedMonth.toString().replace(/^prev-/, '').replace(/^0+/, '');
        if (!normalizedMonth) normalizedMonth = selectedMonth;
        
        // Utilizza la stessa funzione che viene usata per caricare i dati nella tabella
        const { getUserWorkHoursByMonth } = await import('../firebase');
        const workHoursData = await getUserWorkHoursByMonth(userData.id, normalizedMonth, selectedYear);
        
        if (workHoursData && workHoursData.entries && workHoursData.entries.length > 0) {
          const entries = workHoursData.entries;
          
          // Calcola i giorni lavorati (escludendo lettere speciali M, P, F, A, C) - AGGIUNTA F
          const completedDays = entries.filter(entry => 
            entry.total !== "M" && entry.total !== "P" && entry.total !== "F" && entry.total !== "A" && entry.total !== "C" &&
            (parseFloat(entry.total) > 0 || parseFloat(entry.overtime || 0) > 0)
          ).length;
          
          // Calcola il totale delle ore standard (escludendo lettere speciali M, P, F, A, C) - AGGIUNTA F
          const totalStandardHours = entries.reduce((sum, entry) => {
            if (["M", "P", "F", "A", "C"].includes(entry.total)) {
              return sum;
            }
            return sum + (parseFloat(entry.total) || 0);
          }, 0);
          
          // Calcola il totale delle ore di straordinario
          const totalOvertimeHours = entries.reduce((sum, entry) => {
            return sum + (parseFloat(entry.overtime || 0) || 0);
          }, 0);
          
          // Conta i giorni "speciali" - AGGIUNTA FERIE
          const malattiaCount = entries.filter(entry => entry.total === "M").length;
          const permessoCount = entries.filter(entry => entry.total === "P").length;
          const ferieCount = entries.filter(entry => entry.total === "F").length; // ← NUOVO
          const assenzaCount = entries.filter(entry => entry.total === "A").length;
          const cigCount = entries.filter(entry => entry.total === "C").length;
          
          setMonthlyStats({
            month: selectedMonth,
            year: selectedYear,
            totalDays: entries.length,
            completedDays,
            totalStandardHours,
            totalOvertimeHours,
            totalHours: totalStandardHours + totalOvertimeHours,
            malattiaCount,
            permessoCount,
            ferieCount, // ← NUOVO
            assenzaCount,
            cigCount
          });
        } else {
          // Se non ci sono dati, imposta statistiche a zero
          setMonthlyStats({
            month: selectedMonth,
            year: selectedYear,
            totalDays: 0,
            completedDays: 0,
            totalStandardHours: 0,
            totalOvertimeHours: 0,
            totalHours: 0,
            malattiaCount: 0,
            permessoCount: 0,
            ferieCount: 0, // ← NUOVO
            assenzaCount: 0,
            cigCount: 0
          });
        }
      } catch (err) {
        console.error("Errore caricamento statistiche mensili:", err);
        // Non impostare errore - questa è una funzionalità non critica
        
        // Imposta statistiche a zero in caso di errore
        setMonthlyStats({
          month: selectedMonth,
          year: selectedYear,
          totalDays: 0,
          completedDays: 0,
          totalStandardHours: 0,
          totalOvertimeHours: 0,
          totalHours: 0,
          malattiaCount: 0,
          permessoCount: 0,
          ferieCount: 0, // ← NUOVO
          assenzaCount: 0,
          cigCount: 0
        });
      } finally {
        setIsLoadingStats(false);
      }
    };
    
    fetchMonthlyStats();
  }, [userData, selectedMonth, selectedYear]);

  // Funzione per ottenere il nome del mese in italiano
  const getMonthName = (month) => {
    const monthNames = [
      '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];
    return monthNames[parseInt(month)] || '';
  };

  // Gestisce il cambio del mese selezionato
  const handleMonthChange = (e) => {
    const selectedValue = e.target.value;
    const selectedMonthData = availableMonths.find(month => month.value === selectedValue);
    
    if (selectedMonthData) {
      setSelectedMonth(selectedValue);
      setSelectedYear(selectedMonthData.year);
    }
  };

  return (
    <div className="user-info-container">
      <div className="user-info">
        <div className="user-info-details">
          <h2>Informazioni Utente</h2>
          <p className="selection-description">
            Visualizzazione dati per {getMonthName(selectedMonth)} {selectedYear}
          </p>
        </div>
        
        <div className="user-actions">
          <div className="month-selector">
            <label htmlFor="month-select">Mese:</label>
            <select 
              id="month-select" 
              value={selectedMonth || ''} 
              onChange={handleMonthChange}
            >
              {availableMonths.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            className="qr-toggle-btn"
            onClick={() => setShowQRCode(!showQRCode)}
          >
            {showQRCode ? "Nascondi QR Code" : "Mostra QR Code"}
          </button>
        </div>
      </div>
      
      {/* QR Code container */}
      {showQRCode && (
        <div className="qrcode-container">
          <UserQRCode />
        </div>
      )}
      
      {/* Monthly Statistics Card - Now using the same data source as the timesheet table */}
      {monthlyStats && !isLoadingStats && (
        <div className="monthly-stats-card">
          <h3>Statistiche di {getMonthName(monthlyStats.month)} {monthlyStats.year}</h3>
          
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{monthlyStats.completedDays}</div>
              <div className="stat-label">Giorni Lavorati</div>
            </div>
            
            {/* Sezione Ore Totali e Ore Straordinario rimossa per visualizzazione semplificata */}
            
            {monthlyStats.malattiaCount > 0 && (
              <div className="stat-item">
                <div className="stat-value">{monthlyStats.malattiaCount}</div>
                <div className="stat-label">Giorni Malattia</div>
              </div>
            )}
            
            {monthlyStats.permessoCount > 0 && (
              <div className="stat-item">
                <div className="stat-value">{monthlyStats.permessoCount}</div>
                <div className="stat-label">Giorni Permesso</div>
              </div>
            )}
            
            {/* NUOVO: Visualizzazione ferie */}
            {monthlyStats.ferieCount > 0 && (
              <div className="stat-item">
                <div className="stat-value">{monthlyStats.ferieCount}</div>
                <div className="stat-label">Giorni Ferie</div>
              </div>
            )}
            
            {monthlyStats.assenzaCount > 0 && (
              <div className="stat-item">
                <div className="stat-value">{monthlyStats.assenzaCount}</div>
                <div className="stat-label">Giorni Assenza</div>
              </div>
            )}

            {monthlyStats.cigCount > 0 && (
              <div className="stat-item">
                <div className="stat-value">{monthlyStats.cigCount}</div>
                <div className="stat-label">Cassa Integrazione</div>
              </div>
            )}
          </div>
          
          {/* AGGIORNATA: Nota con ferie (semplificata) */}
          <div className="stat-note">
            Riepilogo calcolato dai dati della tabella. Mostra giorni lavorati e giorni speciali (M=Malattia, P=Permesso, F=Ferie, A=Assenza).
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInfo;